'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo } from 'react';
import { FORM_VIEW_THRESHOLD, LEAD_FORM_EVENT } from '@/app/lib/forms/tracking/constants';
import { trackLeadFormEvent } from '@/app/lib/forms/tracking/track-lead-form-event';
import type { FieldIssue, FormTracker, LeadFormEventName, LeadFormPayload, LeadFormTrackingMeta } from '@/app/lib/forms/tracking/types';
const noop = () => undefined;
const NOOP_TRACKER: FormTracker = {
  enabled: false,
  observeView: noop,
  onFieldChange: noop,
  onFieldBlur: noop,
  onSubmit: noop,
  onStep: noop,
  onSuccess: noop,
  onFailed: noop
};
type FormTrackerInstance = FormTracker & {
  setMeta: (meta?: LeadFormTrackingMeta) => void;
};
const isEmpty = (value: unknown) => value === undefined || value === null || value === '' || value === false || Array.isArray(value) && value.length === 0;

/** Empty fields are `required`; otherwise the zod issue code decides. */
const toErrorType = (issue: FieldIssue, value: unknown) => {
  if (isEmpty(value)) return 'required';
  switch (issue.code) {
    case 'too_small':
      return 'too_short';
    case 'too_big':
      return 'too_long';
    case 'invalid_format':
      return 'invalid_format';
    default:
      return 'invalid';
  }
};
const createSessionId = () => typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/** One instance per mounted form: the Sets keep the once-only events once. */
const createFormTracker = (): FormTrackerInstance => {
  let currentMeta: LeadFormTrackingMeta | undefined;
  const getMeta = () => currentMeta;
  const sessionId = createSessionId();
  const interacted = new Set<string>();
  const completed = new Set<string>();
  // Fields whose current value already sent a blur error.
  const blurReported = new Set<string>();
  let initiatedAt: number | undefined;
  // Last valid submit: the request goes out right after it.
  let submittedAt: number | undefined;
  let isViewed = false;
  let observer: IntersectionObserver | undefined;
  const send = (eventName: LeadFormEventName, payload: LeadFormPayload) => {
    const meta = getMeta();
    if (!meta) return;
    trackLeadFormEvent(eventName, {
      group: meta.formId,
      // Read by the GTM tag.
      group_name: meta.formName,
      step_name: meta.stepName,
      form_id: meta.formId,
      form_name: meta.formName,
      form_type: meta.formType,
      offer_url: meta.offerUrl,
      form_session_id: sessionId,
      ...payload
    });
  };
  const formName = () => getMeta()?.formName ?? '';
  // Label for `text`, else the key.
  const fieldLabel = (field: string) => getMeta()?.fieldLabels?.[field] ?? field;
  const sendFieldError = (field: string, value: unknown, issue: FieldIssue, trigger: 'blur' | 'submit') => {
    const errorType = toErrorType(issue, value);
    send(LEAD_FORM_EVENT.fieldValidationError, {
      text: fieldLabel(field),
      value: errorType,
      field_id: field,
      error_type: errorType,
      error_message: issue.message,
      trigger
    });
  };
  const markViewed = () => {
    isViewed = true;
    observer?.disconnect();
    observer = undefined;
    send(LEAD_FORM_EVENT.view, {
      text: formName()
    });
  };

  // An explicit time wins; otherwise time since the valid submit.
  const toResponseMs = (responseMs?: number) => {
    if (responseMs !== undefined) return Math.round(responseMs);
    if (submittedAt === undefined) return undefined;
    return Math.round(performance.now() - submittedAt);
  };
  return {
    enabled: true,
    setMeta: meta => {
      currentMeta = meta;
    },
    observeView: element => {
      if (!element) {
        observer?.disconnect();
        observer = undefined;
        return;
      }
      if (isViewed || typeof IntersectionObserver === 'undefined') return;
      observer?.disconnect();
      observer = new IntersectionObserver(entries => {
        // Half the form on screen - or, for a form taller than two screens,
        // half the screen filled by it.
        const isSeen = entries.some(entry => entry.intersectionRatio >= FORM_VIEW_THRESHOLD || !!entry.rootBounds && entry.intersectionRect.height >= entry.rootBounds.height * FORM_VIEW_THRESHOLD);
        if (isSeen && !isViewed) markViewed();
      }, {
        threshold: [0, 0.25, FORM_VIEW_THRESHOLD, 1]
      });
      observer.observe(element);
    },
    onFieldChange: (field, value, issue) => {
      // Typing implies seen: keeps view >= initiated.
      if (!isViewed) markViewed();
      if (initiatedAt === undefined) {
        initiatedAt = performance.now();
        send(LEAD_FORM_EVENT.initiated, {
          text: formName()
        });
      }
      if (!interacted.has(field)) {
        interacted.add(field);
        send(LEAD_FORM_EVENT.fieldInteracted, {
          text: fieldLabel(field),
          field_id: field
        });
      }
      blurReported.delete(field);
      if (!issue && !isEmpty(value) && !completed.has(field)) {
        completed.add(field);
        send(LEAD_FORM_EVENT.fieldCompleted, {
          text: fieldLabel(field),
          value: getMeta()?.valueFields?.includes(field) ? getMeta()?.formatValue?.(field, value) ?? String(value) : field,
          field_id: field
        });
      }
    },
    onFieldBlur: (field, value, issue) => {
      // Only fields the customer touched, once per value.
      if (!issue || !interacted.has(field) || blurReported.has(field)) return;
      blurReported.add(field);
      sendFieldError(field, value, issue, 'blur');
    },
    onSubmit: ({
      isValid,
      issues,
      values
    }) => {
      const errorFields = Object.keys(issues);
      if (isValid) submittedAt = performance.now();
      send(LEAD_FORM_EVENT.submit, {
        text: formName(),
        value: isValid ? getMeta()?.submitPassValue ?? 'pass' : 'fail',
        validation_result: isValid ? 'pass' : 'fail',
        form_fill_duration_ms: initiatedAt === undefined ? undefined : Math.round(performance.now() - initiatedAt),
        error_fields: errorFields.length ? errorFields.join(',') : undefined
      });
      errorFields.forEach(field => {
        const issue = issues[field];
        if (issue) sendFieldError(field, values[field], issue, 'submit');
      });
      if (!isValid) {
        send(LEAD_FORM_EVENT.validationFailed, {
          text: formName()
        });
      }
    },
    onStep: eventName => {
      send(eventName, {
        text: formName()
      });
    },
    onSuccess: ({
      leadId,
      responseMs
    }) => {
      send(LEAD_FORM_EVENT.success, {
        text: formName(),
        value: 'success',
        lead_id: leadId,
        submit_response_ms: toResponseMs(responseMs)
      });
    },
    onFailed: ({
      errorType,
      responseMs
    }) => {
      send(LEAD_FORM_EVENT.failed, {
        text: formName(),
        value: errorType,
        error_type: errorType,
        submit_response_ms: toResponseMs(responseMs)
      });
    }
  };
};

/** `lead-*` funnel tracking; a no-op without `meta`. Pass to `useForm({ tracking })`. */
export const useFormTracking = meta => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = createFormTracker();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const tracker = t0;
  let t1;
  if ($[1] !== meta) {
    t1 = () => {
      tracker.setMeta(meta);
    };
    $[1] = meta;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(t1);
  return meta ? tracker : NOOP_TRACKER;
};